/**
 * DataListSection — fetches records and renders a vertical list.
 * Supports: section title/description, avatar style/color, dividers, Show More button.
 */

import React from 'react';
import { usePublicTableList } from '../../../hooks/usePublicTableView';
import DataSectionWrapper from '../DataSectionWrapper';

export default function DataListSection({ config, dataBinding, theme }) {
  const cfg       = config || {};
  const tableSlug = dataBinding?.table_slug;
  const fieldMap  = dataBinding?.field_mapping || {};
  const limit     = dataBinding?.limit || 10;

  const avatarShape   = cfg.avatar_shape || 'circle';
  const avatarColor   = cfg.avatar_color || (theme?.primary || 'var(--primary-color)');
  const showDividers  = cfg.show_dividers !== false;
  const showArrow     = cfg.show_arrow || false;
  const itemLayout    = cfg.item_layout || 'row';

  const { data, isLoading, isError } = usePublicTableList(tableSlug, { page_size: limit });
  const records = data?.records || data?.results || [];

  const viewMapping = data?.view_config?.field_mapping || {};
  const { title: titleField, description: descField } = { ...viewMapping, ...fieldMap };

  if (!tableSlug) return null;

  if (isLoading) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 56, background: '#f1f5f9', borderRadius: 8, marginBottom: 8, opacity: 1 - i * 0.15 }} />
          ))}
        </div>
      </DataSectionWrapper>
    );
  }

  if (isError || records.length === 0) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-muted text-center py-4">
          {isError ? 'Failed to load data.' : 'No records found.'}
        </div>
      </DataSectionWrapper>
    );
  }

  const avatarRadius = avatarShape === 'circle' ? '50%' : avatarShape === 'square' ? 8 : 0;

  return (
    <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
      <div style={itemLayout === 'card' ? { display: 'flex', flexDirection: 'column', gap: 10 } : {}}>
        {records.map((record, idx) => {
          const title = titleField ? record[titleField] : Object.values(record)[0];
          const desc  = descField  ? record[descField]  : Object.values(record)[1];

          const inner = (
            <div
              className="d-flex align-items-center gap-3"
              style={{
                padding: itemLayout === 'card' ? '12px 14px' : '10px 0',
                borderBottom: itemLayout !== 'card' && showDividers && idx < records.length - 1 ? '1px solid #e2e8f0' : 'none',
              }}
            >
              {avatarShape !== 'none' && (
                <div
                  style={{
                    width: 40, height: 40, borderRadius: avatarRadius,
                    background: avatarColor, color: '#fff', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontSize: '1rem',
                  }}
                >
                  {String(title || '?')[0].toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {String(title || '')}
                </div>
                {desc !== undefined && desc !== null && (
                  <div className="text-muted small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(desc)}
                  </div>
                )}
              </div>
              {showArrow && (
                <i className="bi bi-chevron-right text-muted" style={{ fontSize: 13, flexShrink: 0 }} />
              )}
            </div>
          );

          return itemLayout === 'card' ? (
            <div
              key={idx}
              style={{
                background: '#fff',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}
            >
              {inner}
            </div>
          ) : (
            <div key={idx}>{inner}</div>
          );
        })}
      </div>
    </DataSectionWrapper>
  );
}
